import React, { useEffect, useRef, useState } from "react";

export interface DropdownMenuItem {
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}

// Minimal shared dropdown — no menu library existed in this codebase yet (spec:
// expense-capture-ui-redesign). Closes on outside click / Escape / item click.
export function DropdownMenu({ label, title, style, items, align = "left" }: {
  label: React.ReactNode; title?: string; style?: React.CSSProperties;
  items: DropdownMenuItem[]; align?: "left" | "right";
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (items.length === 0) return null;

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex" }}>
      {/* aria-label (not just title) so screen readers — and tests — get a real name
          instead of a raw glyph like "⋯"; title still shows the same text as a tooltip. */}
      <button type="button" title={title} aria-label={title} aria-haspopup="true" aria-expanded={open}
        onClick={() => setOpen(o => !o)} style={style}>
        {label}
      </button>
      {open && (
        <div role="menu" style={{
          position: "absolute", top: "calc(100% + 4px)", [align]: 0, minWidth: 180,
          background: "white", border: "1px solid #e2e8f0", borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 250, overflow: "hidden",
        }}>
          {items.map((item, i) => (
            <button key={i} role="menuitem" type="button" disabled={item.disabled}
              onClick={() => { setOpen(false); item.onClick(); }}
              style={{
                display: "block", width: "100%", textAlign: "left", padding: "9px 14px",
                background: "none", border: "none", whiteSpace: "nowrap",
                cursor: item.disabled ? "not-allowed" : "pointer",
                fontSize: 13, color: item.disabled ? "#cbd5e1" : item.danger ? "#dc2626" : "#1e293b",
              }}
              onMouseEnter={e => { if (!item.disabled) e.currentTarget.style.background = "#f8fafc"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "none"; }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
